import { GetStaticPaths, GetStaticProps } from 'next';

import { PageInformationFieldsFragment, PageSlugFieldsFragment, PasswordPageFieldsFragment } from 'api/graphql-types';
import { getServerPageGet, getServerPageGetSlugs } from 'api/graphql-helper';
import client, { httpLink, setPreviewSecret } from 'api/apollo-client';

import Layout from 'modules/layout/layout';
import { ModuleBuilder } from 'modules/module-builder/module-builder';
import { Head } from 'modules/head/head';
import IntroHeader, { IModule } from 'modules/intro-header/intro-header';
import { BaseModule } from 'modules/base-module/base-module';

import { getStrapiParametersFromContext } from 'utils/strapi-parameters';
import { IBasePageProps } from 'types/page';
import usePageProtection, { IUsePageProtectionVariables } from 'utils/hooks/use-page-protection';
import PasswordLayout from 'modules/password-layout';
import { buildPasswordProtectionStaticProps } from 'utils/password-protection-static';
import IFrame from 'components/iframe/iframe';
import { logger } from 'utils/logger';

interface ICatchAllPageProps extends IBasePageProps {
	page?: PageSlugFieldsFragment;
	pageInformation?: PageInformationFieldsFragment;
	id?: string;
	variables?: IUsePageProtectionVariables;
	isPasswordProtected?: boolean;
	passwordPageLayout: PasswordPageFieldsFragment | null;
}

const CatchAllPage = (props: ICatchAllPageProps): React.JSX.Element => {
	const { page, PasswordModule } = usePageProtection({
		pageData: props.page || null,
		pageQuery: () => getServerPageGet({ variables: props.variables }, client)
	});

	return (
		<Layout
			contentId={props?.id}
			typeName={page?.__typename}
			navbar={props?.pageInformation?.navbar}
			footer={props?.pageInformation?.footer}
		>
			<Head seoModule={page?.seo} seoParent={props?.pageInformation?.seo} />
			{page && page.__typename === 'Page' ? (
				<>
					<BaseModule
						defaultModuleOptions={{
							backgroundColor: page.header?.backgroundColor || 'white',
							padding: 'none'
						}}
					>
						<IntroHeader module={page.header as IModule} slug={page.slug} />
					</BaseModule>
					<ModuleBuilder modules={page.modules || []} slug={page.slug} />
				</>
			) : props.isPasswordProtected ? (
				<PasswordLayout
					locale={props.variables?.locale}
					slug={props.variables?.slug}
					pageData={props.passwordPageLayout}
				>
					<PasswordModule />
				</PasswordLayout>
			) : null}
			{page && page.slug === '/vraag' && (
				<>
					<div>
						<iframe
							style={{ width: '100%', height: '2350px', overflow: 'hidden' }}
							src="https://gelderland.custhelp.com/app/ask"
							seamless={true}
							scrolling="no"
							frameBorder="0"
						></iframe>
					</div>
				</>
			)}
			{page && page.slug === '/melding' && (
				<>
					<div>
						<IFrame
							src="https://gelderland.custhelp.com/app/melding"
							height={4000}
							seamless
							autoHeight
							disableAutoHeightOnMobile
						/>
					</div>
				</>
			)}
		</Layout>
	);
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
	try {
		const pageSlugsResult = await getServerPageGetSlugs({}, client);
		const slugResult = pageSlugsResult.props.data?.findAllSlugs;

		const paths = slugResult?.map((slugObject) => ({
			params: {
				slug: slugObject?.slug?.split('/').filter((slug) => slug !== '') || []
			},
			locale: slugObject?.locale || ctx.defaultLocale
		}));

		return {
			paths: paths || [],
			fallback: 'blocking'
		};
	} catch (error) {
		logger.error('Failed to generate static paths for Gelderland pages', error as Error, {
			component: 'getStaticPaths',
			site: 'gelderland'
		});

		// Return empty paths to allow fallback rendering
		return {
			paths: [],
			fallback: 'blocking'
		};
	}
};

export const getStaticProps: GetStaticProps = async (ctx) => {
	try {
		const { locale, draftMode, publicationState } = getStrapiParametersFromContext(ctx);
		const slug = ctx.params?.slug as string[];
		const fullSlug = `/${slug.join('/')}`;

		const nonPageExtensions = [
			'.jpg',
			'.jpeg',
			'.png',
			'.gif',
			'.svg',
			'.webp',
			'.ico',
			'.bmp',
			'.tiff',
			'.map',
			'.css',
			'.js',
			'.woff',
			'.woff2',
			'.ttf',
			'.eot',
			'.otf',
			'.pdf',
			'.zip',
			'.json',
			'.xml',
			'.txt'
		];
		const hasNonPageExtension = nonPageExtensions.some((ext) => fullSlug.toLowerCase().endsWith(ext));

		if (fullSlug.includes('assets') || hasNonPageExtension) {
			return {
				notFound: true
			};
		}

		const variables = {
			slug: fullSlug,
			locale,
			publicationState
		};

		if (publicationState === 'DRAFT') {
			client.setLink(setPreviewSecret(process.env.STRAPI_PREVIEW_SECRET as string).concat(httpLink));
		}

		const passwordResult = await buildPasswordProtectionStaticProps<any, any, any>({
			fullSlug: fullSlug,
			locale,
			publicationState,
			draftMode,
			contentTypeUid: 'api::page.page',
			contentTypeName: 'page',
			fetchPage: () => getServerPageGet({ variables }, client),
			extractInformation: (data) => data?.props?.data?.pageInformation?.data?.attributes,
			extractPage: (data) => {
				const node = data?.props?.data?.pageBySlug?.data;
				return node ? { documentId: node.documentId, attributes: node.attributes } : null;
			}
		});

		if (!passwordResult.isPasswordProtected && !passwordResult.page) {
			// eslint-disable-next-line no-console
			console.error(`No page found for slug ${fullSlug}`);
			return { notFound: true };
		}

		return {
			props: {
				id: passwordResult.page?.documentId || null,
				variables,
				page: passwordResult.page,
				pageInformation: passwordResult.pageInformation,
				isPasswordProtected: passwordResult.isPasswordProtected,
				passwordPageLayout: passwordResult.passwordPageLayout,
				initialReduxState: passwordResult.initialReduxState
			},
			revalidate: Number(process.env.NEXT_PUBLIC_REVALIDATE_TIME_IN_SECONDS)
		};
	} catch (error) {
		const slug = ctx.params?.slug as string[];
		const fullSlug = slug ? `/${slug.join('/')}` : 'unknown';
		const { locale } = getStrapiParametersFromContext(ctx);

		logger.staticPropsError('Failed to generate static props for Gelderland page', error as Error, fullSlug, locale, {
			params: ctx.params,
			preview: ctx.preview,
			site: 'gelderland'
		});

		return {
			notFound: true
		};
	}
};

export default CatchAllPage;
